// Office notification helpers
import {
  collection,
  getFirestore,
  WhereFilterOp,
  OrderByDirection,
  query as firestoreQuery,
  limit,
  orderBy,
  where,
  getDocs,
  getDoc,
  doc,
  addDoc,
  Timestamp,
  FieldValue,
  updateDoc,
  deleteDoc,
  setDoc
} from 'firebase/firestore'

import Database from '../types/Database'
import FirestoreCollectionPaths from '../types/FirestoreCollectionPaths'

export async function getOfficesManagedByUser(userId: string) {
  console.log('getOfficesManagedByUser called with userId:', userId)
  try {
    // Get offices where user is owner
    const ownedOffices = await query({
      collection: 'offices',
      where: ['uid', '==', userId]
    })
    console.log('Owned offices:', ownedOffices)

    // Try to get offices where user is a manager, but handle errors gracefully
    let managedOffices: any[] = []
    try {
      const managedOfficeRecords = await query({
        collection: 'officeManagers',
        where: ['userId', '==', userId]
      })
      console.log('Managed office records:', managedOfficeRecords)

      const managedOfficeIds = managedOfficeRecords.map((m: any) => m.officeId)
      console.log('managed office IDs:', managedOfficeIds)

      if (managedOfficeIds.length > 0) {
        managedOffices = await query({
          collection: 'offices',
          where: ['oid', 'in', managedOfficeIds]
        })
        console.log('Managed offices:', managedOffices)
      }
    } catch (managerError) {
      console.warn(
        'Could not fetch managed offices (this is OK if user is not a manager):',
        managerError
      )
      managedOffices = []
    }

    // Combine and deduplicate offices
    const allOffices = [...ownedOffices, ...managedOffices]
    const uniqueOffices = allOffices.filter(
      (office, index, self) =>
        index === self.findIndex(o => o.oid === office.oid)
    )

    // Filter out soft-deleted offices
    const activeOffices = uniqueOffices.filter(
      (office: any) => !office.isDeleted
    )

    console.log('Final unique offices:', activeOffices)
    return activeOffices
  } catch (error) {
    console.error('Error fetching offices managed by user:', error)
    return []
  }
}

export async function addOfficeNotification(
  officeId: string,
  text: string,
  date: string,
  createdBy: string
) {
  return await addItem('officeNotifications', {
    officeId,
    text,
    date,
    createdBy
  } as Omit<Database.OfficeNotification, 'id'>)
}

export async function getOfficeNotificationsByDate(
  startDate: string,
  endDate?: string
) {
  // Get all notifications for a given date or date range
  if (endDate) {
    // Query for date range
    return await query({
      collection: 'officeNotifications',
      where: [
        ['date', '>=', startDate],
        ['date', '<=', endDate]
      ]
    })
  } else {
    // Query for single date
    return await query({
      collection: 'officeNotifications',
      where: ['date', '==', startDate]
    })
  }
}

const getById = async <T extends keyof FirestoreCollectionPaths>(
  collectionPath: T,
  recordId: string
) => {
  const db = getFirestore()
  const docRef = doc(db, collectionPath, recordId)
  const result = await getDoc(docRef)
  if (result.exists()) {
    return {
      id: result.id,
      ...result.data()
    } as FirestoreCollectionPaths[T]
  }
  return undefined
}

type WhereClause<T extends keyof FirestoreCollectionPaths> = [
  Extract<keyof Omit<FirestoreCollectionPaths[T], 'id'>, string>,
  WhereFilterOp,
  any
]
type OrderByClause<T> = [Extract<T, string>, OrderByDirection?]

async function query<T extends keyof FirestoreCollectionPaths>({
  collection: collectionPath,
  where: whereClause = [],
  orderBy: orderByClause,
  limit: limitClause = 1000
}: {
  collection: T
  where?: WhereClause<T>[] | WhereClause<T>
  orderBy?: OrderByClause<keyof Omit<FirestoreCollectionPaths[T], 'id'>>
  limit?: number
}) {
  const db = getFirestore()
  if (isSingleWhereClause(whereClause)) {
    whereClause = [whereClause]
  }

  const optionalQueryOptions = [
    ...whereClause.map(w => where(...w)),
    orderByClause ? orderBy(...orderByClause) : undefined
  ].filter(isNotUndefined)

  const q = firestoreQuery<any>(
    collection(db, collectionPath),
    ...optionalQueryOptions,
    limit(limitClause)
  )

  const result = await getDocs<FirestoreCollectionPaths[T]>(q)
  return result.docs.map(item => ({
    ...item.data(),
    id: item.id
  }))
}

function isNotUndefined<T>(val: T | undefined): val is T {
  return !!val
}

function isSingleWhereClause(
  whereClause: WhereClause<any> | WhereClause<any>[]
): whereClause is WhereClause<any> {
  return whereClause.length > 0 && !Array.isArray(whereClause[0])
}

// server timestamps must be Firestore Fieldvalues
type TimestampToFieldValue<T> = {
  [K in keyof T]: T[K] extends Timestamp ? FieldValue : T[K]
}

const addItem = async <T extends keyof FirestoreCollectionPaths>(
  collectionPath: T,
  item: TimestampToFieldValue<Omit<FirestoreCollectionPaths[T], 'id'>>
) => {
  const firestore = getFirestore()
  var setId = (item as any).id
  try {
    if (setId) {
      const result = await setDoc(doc(firestore, collectionPath, setId), item)
      return result // as FirestoreCollectionPaths[T]
    } else {
      const result = await addDoc(collection(firestore, collectionPath), item)
      return result // as FirestoreCollectionPaths[T]
    }
  } catch (e) {
    console.error(`Failed to add item to ${collectionPath}:`, e)
    console.error('Item data:', item)
    throw new Error(`Failed to add item to ${collectionPath}: ${e}`)
  }
}

const updateItem = async <T extends keyof FirestoreCollectionPaths>(
  collectionPath: T,
  itemId: string,
  item: Partial<TimestampToFieldValue<Omit<FirestoreCollectionPaths[T], 'id'>>>
) => {
  const firestore = getFirestore()
  return await updateDoc(doc(firestore, collectionPath, itemId), item)
}

const deleteItem = async <T extends keyof FirestoreCollectionPaths>(
  collectionPath: T,
  itemId: string
) => {
  const firestore = getFirestore()
  return await deleteDoc(doc(firestore, collectionPath, itemId))
}

// Subscription-specific queries

/**
 * Get the subscription for a specific office
 * @param officeId - The office ID to get the subscription for
 * @returns The subscription record or null if not found
 */
export async function getOfficeSubscription(
  officeId: string
): Promise<Database.Subscription | null> {
  const subscriptions = await query({
    collection: 'subscriptions',
    where: ['officeId', '==', officeId],
    limit: 1
  })

  return subscriptions.length > 0 ? subscriptions[0] : null
}

/**
 * Create a new subscription record
 * @param subscription - The subscription data to create
 * @returns The created subscription document reference
 */
export async function createSubscription(
  subscription: Omit<Database.Subscription, 'id'>
) {
  return await addItem('subscriptions', {
    ...subscription,
    createdAt: Timestamp.now() as any,
    updatedAt: Timestamp.now() as any
  })
}

/**
 * Update an existing subscription record
 * @param subscriptionId - The subscription ID to update
 * @param updates - The fields to update
 */
export async function updateSubscription(
  subscriptionId: string,
  updates: Partial<Omit<Database.Subscription, 'id' | 'createdAt'>>
) {
  return await updateItem('subscriptions', subscriptionId, {
    ...updates,
    updatedAt: Timestamp.now() as any
  })
}

/**
 * Get all subscriptions for a specific user (office owner)
 * Used for billing portal to show all subscriptions the user owns
 * @param userId - The user ID (office owner)
 * @returns Array of subscription records
 */
export async function getUserSubscriptions(
  userId: string
): Promise<Database.Subscription[]> {
  return await query({
    collection: 'subscriptions',
    where: ['ownerId', '==', userId]
  })
}

/**
 * Get all offices owned by a specific user
 * Used for office selector in subscription purchase flow
 * @param userId - The user ID (office owner)
 * @returns Array of offices where the user is the owner
 */
export async function getOfficesOwnedByUser(
  userId: string
): Promise<Database.Office[]> {
  return await query({
    collection: 'offices',
    where: ['uid', '==', userId]
  })
}

/**
 * Get an office by ID with subscription data included
 * @param officeId - The office ID to retrieve
 * @returns The office with subscription tier and capacity, or undefined if not found
 */
export async function getOffice(
  officeId: string
): Promise<Database.Office | undefined> {
  const office = await getById('offices', officeId)

  if (!office) {
    return undefined
  }

  // If office already has subscription data, return it
  if (office.subscriptionTier && office.userCapacity) {
    return office
  }

  // Otherwise, fetch subscription data and merge it
  const subscription = await getOfficeSubscription(officeId)

  if (subscription) {
    return {
      ...office,
      subscriptionTier: subscription.tier,
      userCapacity: getCapacityForTier(subscription.tier),
      subscriptionId: subscription.id
    }
  }

  // If no subscription found, return office with default free tier
  return {
    ...office,
    subscriptionTier: 'free',
    userCapacity: 5,
    subscriptionId: undefined
  }
}

/**
 * Helper function to get user capacity for a given tier
 * @param tier - The subscription tier
 * @returns The user capacity for that tier
 */
function getCapacityForTier(tier: Database.SubscriptionTier): number {
  const capacities: Record<Database.SubscriptionTier, number> = {
    free: 5,
    small: 20,
    medium: 50,
    large: 200
  }
  return capacities[tier]
}

const db = {
  getById,
  query,
  addItem,
  updateItem,
  deleteItem
}

export default db
